import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, RefreshCw, X, Copy, CheckCircle, ExternalLink, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { ApiError } from '../../shared/api';
import { toast } from 'sonner';

const errorDisplayVariants = cva(
  'w-full',
  {
    variants: {
      variant: {
        default: '',
        destructive: '',
        warning: '',
      },
    },
    defaultVariants: {
      variant: 'destructive',
    },
  }
);

export interface ErrorDisplayProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof errorDisplayVariants> {
  title?: string;
  message?: string;
  error?: Error | ApiError | string;
  onRetry?: () => void;
  onDismiss?: () => void;
  onHome?: () => void;
  showIcon?: boolean;
  showDetails?: boolean;
  showCopyError?: boolean;
  showSuggestions?: boolean;
  retryText?: string;
  dismissText?: string;
  variant?: 'default' | 'destructive' | 'warning';
  suggestions?: string[];
}

const ErrorDisplay = React.forwardRef<HTMLDivElement, ErrorDisplayProps>(
  ({ 
    className, 
    variant = 'destructive',
    title,
    message,
    error,
    onRetry,
    onDismiss,
    onHome,
    showIcon = true,
    showDetails = false,
    showCopyError = false,
    showSuggestions = true,
    retryText,
    dismissText,
    suggestions,
    ...props 
  }, ref) => {
    const { t } = useTranslation();
    const [showFullError, setShowFullError] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const errorInfo = React.useMemo(() => {
      if (!error && !message) return null;
      
      if (message) {
        return {
          message,
          type: 'custom',
          status: undefined,
          suggestions: suggestions || [],
        };
      }

      if (typeof error === 'string') {
        return {
          message: error,
          type: 'generic',
          status: undefined,
          suggestions: suggestions || [],
        };
      }

      if (error instanceof ApiError) {
        const errorSuggestions: string[] = [...(suggestions || [])];
        
        if (error.isNetworkError) {
          errorSuggestions.push(t('notifications.error.checkConnection'));
          errorSuggestions.push(t('notifications.error.tryAgain'));
        } else if (error.isServerError) {
          errorSuggestions.push(t('notifications.error.contactSupport'));
          errorSuggestions.push(t('notifications.error.refreshPage'));
        } else if (error.isUnauthorized) {
          errorSuggestions.push('Try logging out and logging back in');
          errorSuggestions.push(t('notifications.error.contactSupport'));
        } else if (error.isForbidden) {
          errorSuggestions.push('Contact your administrator for additional permissions');
        } else if (error.isNotFound) {
          errorSuggestions.push('The item may have been deleted or moved');
          errorSuggestions.push(t('notifications.error.refreshPage'));
        } else if (error.isValidationError) {
          errorSuggestions.push('Check your input and try again');
          errorSuggestions.push('Ensure all required fields are filled correctly');
        }

        return {
          message: error.message,
          type: 'api',
          status: error.status,
          suggestions: errorSuggestions,
          fullError: error,
        };
      }

      if (error instanceof Error) {
        return {
          message: error.message || t('errors.unknown'),
          type: 'generic',
          status: undefined,
          suggestions: suggestions || [],
          fullError: error,
        };
      }

      return {
        message: t('errors.unknown'),
        type: 'unknown',
        status: undefined,
        suggestions: suggestions || [],
      };
    }, [error, message, suggestions, t]);

    const copyErrorToClipboard = React.useCallback(async () => {
      if (!errorInfo?.fullError) return;

      const errorDetails = {
        message: errorInfo.message,
        type: errorInfo.type,
        status: errorInfo.status,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        stack: errorInfo.fullError instanceof Error ? errorInfo.fullError.stack : undefined,
      };

      try {
        await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
        setCopied(true);
        toast.success('Error details copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy error details');
      }
    }, [errorInfo]);

    const errorTitle = title || t('common.error');
    const finalRetryText = retryText || t('notifications.error.tryAgain');
    const finalDismissText = dismissText || t('common.close');

    if (!errorInfo) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(errorDisplayVariants({ variant, className }))}
        {...props}
      >
        <Alert variant={variant === 'destructive' ? 'destructive' : variant === 'warning' ? 'default' : 'default'}>
          <div className="flex items-start gap-2">
            {showIcon && <AlertTriangle className="h-4 w-4 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <AlertTitle className="flex items-center justify-between">
                <span>{errorTitle}</span>
                {errorInfo.status && (
                  <span className="text-xs font-mono bg-destructive/10 px-2 py-1 rounded">
                    {errorInfo.status}
                  </span>
                )}
              </AlertTitle>
              <AlertDescription className="mt-1 space-y-3">
                <p>{errorInfo.message}</p>
                
                {showSuggestions && errorInfo.suggestions.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Suggestions:</p>
                    <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                      {errorInfo.suggestions.map((suggestion, index) => (
                        <li key={index}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {showDetails && errorInfo.fullError && (
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullError(!showFullError)}
                      className="h-6 p-0 text-xs"
                    >
                      {showFullError ? 'Hide' : 'Show'} technical details
                    </Button>
                    
                    {showFullError && (
                      <div className="bg-muted p-3 rounded text-xs font-mono overflow-auto max-h-32">
                        <pre className="whitespace-pre-wrap">
                          {errorInfo.fullError instanceof Error 
                            ? errorInfo.fullError.stack || errorInfo.fullError.message
                            : JSON.stringify(errorInfo.fullError, null, 2)
                          }
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </AlertDescription>
              
              {(onRetry || onDismiss || onHome || showCopyError) && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {onRetry && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRetry}
                      className="h-8"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      {finalRetryText}
                    </Button>
                  )}
                  
                  {onHome && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onHome}
                      className="h-8"
                    >
                      <Home className="h-3 w-3 mr-1" />
                      Home
                    </Button>
                  )}

                  {showCopyError && errorInfo.fullError && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyErrorToClipboard}
                      className="h-8"
                    >
                      {copied ? (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      ) : (
                        <Copy className="h-3 w-3 mr-1" />
                      )}
                      {copied ? 'Copied' : 'Copy details'}
                    </Button>
                  )}

                  {errorInfo.type === 'api' && errorInfo.status && errorInfo.status >= 500 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open('/support', '_blank')}
                      className="h-8"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Contact Support
                    </Button>
                  )}
                  
                  {onDismiss && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onDismiss}
                      className="h-8"
                    >
                      <X className="h-3 w-3 mr-1" />
                      {finalDismissText}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </Alert>
      </div>
    );
  }
);
ErrorDisplay.displayName = 'ErrorDisplay';

export { ErrorDisplay, errorDisplayVariants };